/**
 * Analysis — Battery Swap Analytics (7-tab dashboard).
 *
 * All data pulled live from `/api/analysis/*`.  Global filter bar sits
 * above the tabs so every tab respects the same date/bike/rider/staff/
 * location scope. Charts are drawn with Recharts (already installed).
 */
import { useEffect, useMemo, useState, Fragment as FragmentAny } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { API, getAuthHeader } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import RiderAnalysis from './RiderAnalysis';
import {
  Activity, Bike, Users, Wrench, Battery, DollarSign, AlertTriangle,
  RefreshCw, Filter, Save, FileDown, FileText as FileTextIcon, CheckCircle2,
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell,
} from 'recharts';

const PALETTE = ['#84cc16', '#0ea5e9', '#f59e0b', '#ef4444', '#8b5cf6',
                 '#14b8a6', '#f97316', '#ec4899'];

const SEVERITY_COLORS = { high: '#ef4444', mid: '#f59e0b', low: '#facc15' };

const KIND_LABELS = {
  typo_spike: 'Typo Spike',
  stat_outlier: 'Statistical Outlier',
  hang: 'SoC Hang',
  minor_negative: 'Minor Negative Δ',
  missing_reading: 'Missing Reading',
  reset: 'Odo Reset',
};

// ---------------------------------------------------------------------------
// Export helpers — Excel via SheetJS, PDF via jsPDF+autoTable
// ---------------------------------------------------------------------------

const exportSheetsToExcel = (fileName, sheets) => {
  // sheets: [{ name, rows: [{col: val}, ...] }]
  const wb = XLSX.utils.book_new();
  sheets.forEach(({ name, rows }) => {
    const ws = XLSX.utils.json_to_sheet(rows || []);
    XLSX.utils.book_append_sheet(wb, ws, name.substring(0, 31) || 'Sheet');
  });
  XLSX.writeFile(wb, fileName);
};

const exportSheetsToPDF = (fileName, title, sheets) => {
  const doc = new jsPDF({ orientation: 'landscape' });
  doc.setFontSize(14);
  doc.text(title, 14, 15);
  let y = 22;
  sheets.forEach(({ name, rows }) => {
    if (!rows || rows.length === 0) return;
    const columns = Object.keys(rows[0]);
    doc.setFontSize(11);
    doc.text(name, 14, y);
    autoTable(doc, {
      startY: y + 2,
      head: [columns],
      body: rows.map(r => columns.map(c => (r[c] ?? ''))),
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [132, 204, 22] },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;
    if (y > 180) { doc.addPage(); y = 15; }
  });
  doc.save(fileName);
};

const ExportBar = ({ data, tabKey, tabLabel }) => {
  const buildSheets = () => {
    if (!data) return [];
    switch (tabKey) {
      case 'overview':
        return [
          { name: 'Totals', rows: [data.totals || {}] },
          { name: 'Daily trend', rows: data.trend || [] },
          { name: 'Top locations', rows: data.top_locations || [] },
          { name: 'Anomaly by kind', rows: data.anomaly_by_kind || [] },
        ];
      case 'bikes-range':
        return [{ name: 'Motorcycles', rows: data.rows || [] }];
      case 'riders':
        return [{ name: 'Riders', rows: data.rows || [] }];
      case 'staff':
        return [{ name: 'Staff', rows: data.rows || [] }];
      case 'batteries':
        return [
          { name: 'Batteries', rows: data.rows || [] },
          { name: 'SoC drop histogram', rows: data.soc_drop_histogram || [] },
        ];
      case 'cost-energy':
        return [
          { name: 'KPIs', rows: [data.kpis || {}] },
          { name: 'Electricity bills', rows: data.electricity_rows || [] },
          { name: 'Rent (pro-rata)', rows: data.rent_rows || [] },
          { name: 'Monthly trend', rows: data.cost_trend || [] },
          { name: 'Cost by location', rows: data.cost_by_location || [] },
        ];
      case 'data-outliers':
        return [
          { name: 'Totals', rows: [data.totals || {}] },
          { name: 'By kind', rows: data.by_kind || [] },
          { name: 'By severity', rows: data.by_severity || [] },
          { name: 'Anomalies', rows: data.anomalies || [] },
        ];
      default:
        return [];
    }
  };
  const stamp = new Date().toISOString().slice(0, 10);
  return (
    <div className="flex items-center justify-end gap-2 mb-2">
      <Button
        size="sm" variant="outline"
        onClick={() => exportSheetsToExcel(`analysis_${tabKey}_${stamp}.xlsx`, buildSheets())}
        data-testid={`analysis-export-excel-${tabKey}`}
      >
        <FileDown className="w-4 h-4 mr-1" /> Excel
      </Button>
      <Button
        size="sm" variant="outline"
        onClick={() => exportSheetsToPDF(`analysis_${tabKey}_${stamp}.pdf`, `Battery Swap Analysis — ${tabLabel}`, buildSheets())}
        data-testid={`analysis-export-pdf-${tabKey}`}
      >
        <FileTextIcon className="w-4 h-4 mr-1" /> PDF
      </Button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Utility widgets
// ---------------------------------------------------------------------------

const StatCard = ({ label, value, sub, icon: Icon, tone = 'lime', onClick }) => {
  const toneMap = {
    lime: 'bg-lime-50 text-lime-700 border-lime-200',
    sky: 'bg-sky-50 text-sky-700 border-sky-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
    violet: 'bg-violet-50 text-violet-700 border-violet-200',
  };
  const clickable = typeof onClick === 'function';
  return (
    <Card
      className={`border ${toneMap[tone]} ${clickable ? 'cursor-pointer hover:shadow-md hover:scale-[1.01] transition-transform' : ''}`}
      onClick={clickable ? onClick : undefined}
      data-testid={`analysis-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
            <div className="text-2xl font-semibold mt-1 tabular-nums">{value}</div>
            {sub && <div className="text-xs opacity-70 mt-1">{sub}</div>}
          </div>
          {Icon && <Icon className="w-6 h-6 opacity-60" />}
        </div>
      </CardContent>
    </Card>
  );
};

const DataTable = ({ columns, rows, testid, onRowClick, sort, onSortChange }) => (
  <div className="overflow-x-auto border rounded-lg" data-testid={testid}>
    <table className="min-w-full text-sm">
      <thead className="bg-slate-50">
        <tr>
          {columns.map(c => {
            const sortable = c.sortable && onSortChange;
            const active = sort && sort.key === c.key;
            const arrow = !active ? '' : (sort.dir === 'asc' ? ' ▲' : ' ▼');
            return (
              <th
                key={c.key}
                className={`px-3 py-2 text-left font-medium text-slate-600 ${c.align === 'right' ? 'text-right' : ''} ${sortable ? 'cursor-pointer select-none hover:text-slate-900' : ''}`}
                onClick={sortable ? () => onSortChange(c.key) : undefined}
                data-testid={sortable ? `sort-${c.key}` : undefined}
              >
                {c.label}{arrow}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={columns.length} className="px-3 py-6 text-center text-slate-400">No data for the selected filters</td></tr>
        ) : rows.map((r, i) => (
          <tr
            key={i}
            className={`border-t hover:bg-slate-50 ${onRowClick ? 'cursor-pointer' : ''}`}
            onClick={onRowClick ? () => onRowClick(r) : undefined}
            data-testid={onRowClick ? `row-${r.bike || r.rider || i}` : undefined}
          >
            {columns.map(c => (
              <td key={c.key} className={`px-3 py-2 ${c.align === 'right' ? 'text-right tabular-nums' : ''}`}>
                {c.render ? c.render(r) : (r[c.key] ?? '—')}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ---------------------------------------------------------------------------
// Filter bar
// ---------------------------------------------------------------------------

const FilterBar = ({ filters, setFilters, options, onRefresh }) => {
  const update = (key, value) => setFilters({ ...filters, [key]: value });
  return (
    <Card className="border-2 border-slate-200">
      <CardContent className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div>
            <Label className="text-xs">From date</Label>
            <Input type="date" value={filters.from_date || ''} onChange={e => update('from_date', e.target.value)} data-testid="analysis-filter-from" />
          </div>
          <div>
            <Label className="text-xs">To date</Label>
            <Input type="date" value={filters.to_date || ''} onChange={e => update('to_date', e.target.value)} data-testid="analysis-filter-to" />
          </div>
          <div>
            <Label className="text-xs">Motorcycle</Label>
            <Input list="analysis-bikes" placeholder="Plate no." value={filters.bike || ''} onChange={e => update('bike', e.target.value)} data-testid="analysis-filter-bike" />
            <datalist id="analysis-bikes">{(options.bikes || []).map(b => <option key={b} value={b} />)}</datalist>
          </div>
          <div>
            <Label className="text-xs">Rider</Label>
            <Input list="analysis-riders" placeholder="Rider name" value={filters.rider || ''} onChange={e => update('rider', e.target.value)} data-testid="analysis-filter-rider" />
            <datalist id="analysis-riders">{(options.riders || []).map(r => <option key={r} value={r} />)}</datalist>
          </div>
          <div>
            <Label className="text-xs">Staff</Label>
            <Input list="analysis-staff" placeholder="Technician" value={filters.staff || ''} onChange={e => update('staff', e.target.value)} data-testid="analysis-filter-staff" />
            <datalist id="analysis-staff">{(options.staff || []).map(s => <option key={s} value={s} />)}</datalist>
          </div>
          <div>
            <Label className="text-xs">Location</Label>
            <Input list="analysis-locations" placeholder="Station" value={filters.location || ''} onChange={e => update('location', e.target.value)} data-testid="analysis-filter-location" />
            <datalist id="analysis-locations">{(options.locations || []).map(l => <option key={l} value={l} />)}</datalist>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setFilters({})} data-testid="analysis-filter-reset">
            <Filter className="w-4 h-4 mr-1" /> Reset filters
          </Button>
          <Button size="sm" onClick={onRefresh} data-testid="analysis-filter-apply">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Individual tab components
// ---------------------------------------------------------------------------

const OverviewTab = ({ data, onDrillDown }) => {
  const [trendPreset, setTrendPreset] = useState('all');
  const [selectedLocations, setSelectedLocations] = useState(new Set());  // empty = show total
  if (!data) return <div className="p-8 text-center text-slate-400">Loading…</div>;
  const t = data.totals || {};
  const trendAll = data.trend || [];
  const trendByLoc = data.trend_by_location || {};
  const locations = Object.keys(trendByLoc).sort();
  const topLocs = data.top_locations || [];
  const anomalyKinds = data.anomaly_by_kind || [];

  const kindLabel = (k) => KIND_LABELS[k] || k;
  const legendFormatter = (v) => <span style={{ fontSize: 11 }}>{KIND_LABELS[v] || v}</span>;

  // Daily-Swaps preset filter (1D / 1W / 1M / 1Y / All)
  const presetDays = { '1d': 1, '1w': 7, '1m': 30, '1y': 365, all: null };
  const now = trendAll.length ? new Date(trendAll[trendAll.length - 1].date) : new Date();
  const cutoff = presetDays[trendPreset]
    ? new Date(now.getTime() - presetDays[trendPreset] * 24 * 60 * 60 * 1000)
    : null;

  const filterByCutoff = (rows) => cutoff ? rows.filter(x => new Date(x.date) >= cutoff) : rows;

  // Build merged data — either total-only OR one column per selected location.
  const showLocationMode = selectedLocations.size > 0;
  let chartData;
  if (showLocationMode) {
    // Combine all dates from selected locations
    const dateSet = new Set();
    selectedLocations.forEach(loc => {
      (trendByLoc[loc] || []).forEach(x => dateSet.add(x.date));
    });
    const dates = Array.from(dateSet).sort();
    chartData = filterByCutoff(dates.map(date => {
      const row = { date };
      selectedLocations.forEach(loc => {
        const found = (trendByLoc[loc] || []).find(x => x.date === date);
        row[loc] = found ? found.swaps : 0;
      });
      return row;
    }));
  } else {
    chartData = filterByCutoff(trendAll);
  }

  const toggleLocation = (loc) => {
    const next = new Set(selectedLocations);
    if (next.has(loc)) next.delete(loc); else next.add(loc);
    setSelectedLocations(next);
  };
  const clearLocations = () => setSelectedLocations(new Set());

  return (
    <div className="space-y-4">
      {/* KPI grid — every card is clickable to open a drill-down */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Swaps" value={(t.swaps ?? 0).toLocaleString()} icon={Activity} tone="lime" onClick={() => onDrillDown?.('swaps')} />
        <StatCard label="Motorcycles" value={t.bikes ?? 0} sub={t.swapping_bikes != null ? `${t.swapping_bikes} swapping` : undefined} icon={Bike} tone="sky" onClick={() => onDrillDown?.('bikes')} />
        <StatCard label="Riders" value={t.riders ?? 0} icon={Users} tone="violet" onClick={() => onDrillDown?.('riders')} />
        <StatCard label="Staff" value={t.staff ?? 0} icon={Wrench} tone="slate" onClick={() => onDrillDown?.('staff')} />
        <StatCard label="Locations" value={t.locations ?? 0} icon={Filter} tone="amber" onClick={() => onDrillDown?.('locations')} />
        <StatCard label="Total km" value={(t.total_km ?? 0).toLocaleString()} sub={`Avg ${t.avg_km_per_swap} km between swaps`} icon={Bike} tone="sky" />
        <StatCard label="Anomalies" value={(t.anomalies ?? 0).toLocaleString()} icon={AlertTriangle} tone="rose" onClick={() => onDrillDown?.('anomalies')} />
        <StatCard label="Non-swapping motorcycles" value={t.non_swapping_bikes ?? '—'} sub="In fleet but no swap log" icon={Bike} tone="slate" onClick={() => onDrillDown?.('non-swapping-bikes')} />
      </div>

      {/* Daily Swaps with date presets */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-base">Daily Swaps</CardTitle>
              <div className="flex gap-1">
                {[
                  { key: '1d', label: '1D' },
                  { key: '1w', label: '1W' },
                  { key: '1m', label: '1M' },
                  { key: '1y', label: '1Y' },
                  { key: 'all', label: 'All' },
                ].map(p => (
                  <Button
                    key={p.key}
                    size="sm"
                    variant={trendPreset === p.key ? 'default' : 'outline'}
                    className="h-7 px-2 text-xs"
                    onClick={() => setTrendPreset(p.key)}
                    data-testid={`analysis-daily-preset-${p.key}`}
                  >
                    {p.label}
                  </Button>
                ))}
              </div>
            </div>
            {/* Location chips — click to compare, click "All" to reset */}
            {locations.length > 0 && (
              <div className="flex items-center gap-1 flex-wrap mt-2">
                <Button
                  size="sm"
                  variant={!showLocationMode ? 'default' : 'outline'}
                  className="h-6 px-2 text-[10px] uppercase tracking-wide"
                  onClick={clearLocations}
                  data-testid="analysis-loc-chip-all"
                >
                  Total
                </Button>
                {locations.map((loc, i) => (
                  <Button
                    key={loc}
                    size="sm"
                    variant={selectedLocations.has(loc) ? 'default' : 'outline'}
                    className="h-6 px-2 text-[10px]"
                    onClick={() => toggleLocation(loc)}
                    style={selectedLocations.has(loc)
                      ? { backgroundColor: PALETTE[i % PALETTE.length], color: '#fff', borderColor: PALETTE[i % PALETTE.length] }
                      : { color: PALETTE[i % PALETTE.length], borderColor: PALETTE[i % PALETTE.length] }}
                    data-testid={`analysis-loc-chip-${loc.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {loc}
                  </Button>
                ))}
              </div>
            )}
          </CardHeader>
          <CardContent style={{ height: 280 }}>
            <ResponsiveContainer>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                {showLocationMode
                  ? Array.from(selectedLocations).map((loc, i) => (
                      <Line
                        key={loc}
                        type="monotone"
                        dataKey={loc}
                        stroke={PALETTE[locations.indexOf(loc) % PALETTE.length]}
                        strokeWidth={2}
                        dot={{ r: 2 }}
                        name={loc}
                      />
                    ))
                  : <Line type="monotone" dataKey="swaps" stroke="#84cc16" strokeWidth={2} dot={{ r: 3 }} />
                }
                {showLocationMode && <Legend verticalAlign="bottom" height={24} iconType="circle" />}
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Anomaly Mix — readable legend below chart, no inline overlapping labels */}
        <Card>
          <CardHeader><CardTitle className="text-base">Anomaly Mix</CardTitle></CardHeader>
          <CardContent style={{ height: 280 }}>
            {anomalyKinds.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-slate-400">No anomalies</div>
            ) : (
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={anomalyKinds}
                    dataKey="count"
                    nameKey="kind"
                    cx="50%"
                    cy="45%"
                    outerRadius={80}
                    labelLine={false}
                    label={false}
                  >
                    {anomalyKinds.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v, _n, p) => [v, kindLabel(p.payload.kind)]} />
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    iconType="circle"
                    formatter={legendFormatter}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Top Locations</CardTitle></CardHeader>
        <CardContent style={{ height: 260 }}>
          <ResponsiveContainer>
            <BarChart data={topLocs}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="location" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="swaps" fill="#0ea5e9" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Bike detail modal — click a motorcycle row to drill down into
// swaps/km/anomalies over time, location breakdown, and a summary card.
// ---------------------------------------------------------------------------
const BIKE_DETAIL_PRESETS = [
  { key: '1d', label: '1D', days: 1, bucket: 'day' },
  { key: '1w', label: '1W', days: 7, bucket: 'day' },
  { key: '1m', label: '1M', days: 30, bucket: 'week' },
  { key: '1y', label: '1Y', days: 365, bucket: 'month' },
  { key: 'all', label: 'Till date', days: null, bucket: 'month' },
];

const BikeDetailModal = ({ bike, onClose }) => {
  const [preset, setPreset] = useState('1m');
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!bike) return;
    let cancelled = false;
    const p = BIKE_DETAIL_PRESETS.find((x) => x.key === preset) || BIKE_DETAIL_PRESETS[2];
    const params = { bucket: p.bucket };
    if (p.days) {
      const to = new Date();
      const from = new Date(to.getTime() - p.days * 86400000);
      params.from_date = from.toISOString().slice(0, 10);
      params.to_date = to.toISOString().slice(0, 10);
    }
    setLoading(true);
    (async () => {
      try {
        const res = await axios.get(
          `${API}/analysis/bikes-range/${encodeURIComponent(bike)}/detail`,
          { params, headers: getAuthHeader() },
        );
        if (!cancelled) setDetail(res.data);
      } catch (_e) {
        if (!cancelled) setDetail(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [bike, preset]);

  const summary = detail?.summary || {};
  const timeline = detail?.timeline || [];
  const locations = detail?.locations || [];

  return (
    <Dialog open={!!bike} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="bike-detail-modal">
        <DialogHeader>
          <DialogTitle data-testid="bike-detail-title">Motorcycle · {bike}</DialogTitle>
          <DialogDescription>Swaps, KM, anomalies and location breakdown</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-1 mt-1">
          {BIKE_DETAIL_PRESETS.map((p) => (
            <Button
              key={p.key}
              size="sm"
              variant={preset === p.key ? 'default' : 'outline'}
              className="h-7 px-2 text-xs"
              onClick={() => setPreset(p.key)}
              data-testid={`bike-detail-preset-${p.key}`}
            >
              {p.label}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="p-6 text-center text-slate-400">Loading…</div>
        ) : !detail ? (
          <div className="p-6 text-center text-slate-400">No data for this range</div>
        ) : (
          <>
            {/* Summary strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
              <div className="border rounded-md p-3 bg-lime-50/60">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">Master Avg Range</div>
                <div className="text-lg font-semibold text-slate-900" data-testid="bike-summary-avg-range">
                  {summary.avg_range_km != null ? `${summary.avg_range_km} km` : '—'}
                </div>
                <div className="text-[11px] text-slate-500">Per swap · SoC-normalized</div>
              </div>
              <div className="border rounded-md p-3 bg-violet-50/60">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">Avg Daily Running</div>
                <div className="text-lg font-semibold text-slate-900" data-testid="bike-summary-avg-daily">
                  {summary.avg_daily_km != null ? `${summary.avg_daily_km} km` : '—'}
                </div>
                <div className="text-[11px] text-slate-500">Over {summary.window_days ?? 0} days</div>
              </div>
              <div className="border rounded-md p-3 bg-sky-50/60">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">Total Swaps</div>
                <div className="text-lg font-semibold text-slate-900" data-testid="bike-summary-total-swaps">
                  {summary.total_swaps ?? 0}
                </div>
                <div className="text-[11px] text-slate-500">
                  Total {summary.total_km ?? 0} km
                </div>
              </div>
              <div className="border rounded-md p-3 bg-rose-50/60">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">Anomalies</div>
                <div className="text-lg font-semibold text-rose-700" data-testid="bike-summary-anomalies">
                  {summary.anomalies ?? 0}
                </div>
                <div className="text-[11px] text-slate-500">
                  Avg SoC drop {summary.avg_soc_drop_pct != null ? `${summary.avg_soc_drop_pct}%` : '—'}
                </div>
              </div>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <Card>
                <CardHeader className="pb-1"><CardTitle className="text-sm">Swaps per {detail.bucket}</CardTitle></CardHeader>
                <CardContent style={{ height: 220 }}>
                  <ResponsiveContainer>
                    <BarChart data={timeline}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Bar dataKey="swaps" fill="#0ea5e9" name="Swaps" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-1"><CardTitle className="text-sm">Total km per {detail.bucket}</CardTitle></CardHeader>
                <CardContent style={{ height: 220 }}>
                  <ResponsiveContainer>
                    <LineChart data={timeline}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="total_km" stroke="#84cc16" strokeWidth={2} name="km" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-1"><CardTitle className="text-sm">Anomalies per {detail.bucket}</CardTitle></CardHeader>
                <CardContent style={{ height: 220 }}>
                  <ResponsiveContainer>
                    <LineChart data={timeline}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                      <Tooltip />
                      <Line type="monotone" dataKey="anomalies" stroke="#ef4444" strokeWidth={2} name="Anomalies" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-1"><CardTitle className="text-sm">Swapping locations</CardTitle></CardHeader>
                <CardContent style={{ height: 220 }}>
                  <ResponsiveContainer>
                    <BarChart data={locations.slice(0, 12)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                      <YAxis dataKey="location" type="category" tick={{ fontSize: 10 }} width={120} />
                      <Tooltip />
                      <Bar dataKey="swaps" fill="#8b5cf6" name="Swaps" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const BikesTab = ({ data, filters, setFilters, onRefresh }) => {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: 'total_km', dir: 'desc' });
  const [detailBike, setDetailBike] = useState(null);
  // Two-level drill-down state for the "kWh dispensed by swap location"
  // table — clicking a location expands its per-month rows, clicking a
  // month expands its per-day rows. Both use plain Sets so multiple
  // rows / months can be open simultaneously.
  const [expandedLocs, setExpandedLocs] = useState(() => new Set());
  const [expandedMonths, setExpandedMonths] = useState(() => new Set());

  const rows = data?.rows || [];

  const handleSort = (key) => {
    setSort((s) => {
      if (s.key !== key) return { key, dir: 'desc' };
      return { key, dir: s.dir === 'desc' ? 'asc' : 'desc' };
    });
  };

  // Client-side filter + sort
  const visibleRows = useMemo(() => {
    let list = rows;
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((r) => (r.bike || '').toLowerCase().includes(q));
    if (sort?.key) {
      const dir = sort.dir === 'asc' ? 1 : -1;
      list = [...list].sort((a, b) => {
        const av = a[sort.key] ?? -Infinity;
        const bv = b[sort.key] ?? -Infinity;
        if (av === bv) return 0;
        return av > bv ? dir : -dir;
      });
    }
    return list;
  }, [rows, search, sort]);

  if (!data) return <div className="p-8 text-center text-slate-400">Loading…</div>;
  const masterAvg = data.master_avg_range_km;
  const masterN = data.master_range_sample_size ?? 0;

  // Date presets — mutate the global filter bar so the whole tab refetches.
  const applyPreset = (key) => {
    const today = new Date();
    const yyyy_mm_dd = (d) => d.toISOString().slice(0, 10);
    if (key === 'all') { setFilters({ ...filters, from_date: '', to_date: '' }); return; }
    const days = { '1d': 0, '1w': 7, '1m': 30, '1y': 365 }[key];
    const from = new Date(today.getTime() - days * 86400000);
    setFilters({ ...filters, from_date: yyyy_mm_dd(from), to_date: yyyy_mm_dd(today) });
  };
  const presetButtons = (
    <div className="flex gap-1 flex-wrap">
      {[
        { key: '1d', label: '1D' }, { key: '1w', label: '1W' },
        { key: '1m', label: '1M' }, { key: '1y', label: '1Y' },
        { key: 'all', label: 'Till date' },
      ].map(p => (
        <Button
          key={p.key}
          size="sm"
          variant="outline"
          className="h-7 px-2 text-xs"
          onClick={() => applyPreset(p.key)}
          data-testid={`analysis-bikes-preset-${p.key}`}
        >
          {p.label}
        </Button>
      ))}
    </div>
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base">Range & Running · date range</CardTitle>
            {presetButtons}
          </div>
        </CardHeader>
      </Card>

      {(masterAvg !== null && masterAvg !== undefined) || data.avg_km_per_bike_per_day != null ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <StatCard
            label="Master Avg Range / Swap"
            value={masterAvg != null ? `${masterAvg} km` : '—'}
            sub={`SoC-normalized · ${masterN} pairs`}
            icon={Bike} tone="lime"
          />
          <StatCard
            label="Avg running / motorcycle / day"
            value={data.avg_km_per_bike_per_day != null ? `${data.avg_km_per_bike_per_day} km` : '—'}
            sub={`Over ${data.window_days ?? 0} days`}
            icon={Activity} tone="violet"
          />
          <StatCard label="Motorcycles analysed" value={rows.length} icon={Bike} tone="sky" />
          <StatCard
            label="Total swaps"
            value={(data.total_swaps ?? rows.reduce((s, r) => s + (r.swaps || 0), 0)).toLocaleString()}
            icon={Activity} tone="violet"
          />
          <StatCard
            label="Avg kWh / motorcycle / day"
            value={data.avg_kwh_per_bike_per_day != null ? `${data.avg_kwh_per_bike_per_day} kWh` : '—'}
            sub={`${data.window_days ?? 0} days · 2 batts × 72 V × Ah`}
            icon={Activity} tone="amber"
          />
          <StatCard
            label="Avg kWh / motorcycle / month"
            value={data.avg_kwh_per_bike_per_month != null ? `${data.avg_kwh_per_bike_per_month} kWh` : '—'}
            sub={`${data.months_in_window ?? 0} months in window`}
            icon={Activity} tone="rose"
          />
          <StatCard
            label="Total kWh dispensed"
            value={data.total_kwh_dispensed != null ? `${Number(data.total_kwh_dispensed).toLocaleString()} kWh` : '—'}
            sub={`${(data.total_swaps || 0).toLocaleString()} swaps × 2 batteries`}
            icon={DollarSign} tone="sky"
          />
        </div>
      ) : null}

      {/* kWh dispensed per swapping location — powers per-day and per-month
          comparisons the operations team uses to plan charger utilisation.
          Two-level drill-down: click a location to expand into month rows,
          click a month to expand into day rows. */}
      {(data.location_energy || []).length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">kWh dispensed by swap location</CardTitle>
              <span className="text-xs text-slate-500">Click a location to see months → click a month to see days</span>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs">
                <tr>
                  <th className="text-left p-2 w-8"></th>
                  <th className="text-left p-2">Location</th>
                  <th className="text-right p-2">Swaps</th>
                  <th className="text-right p-2">Total kWh</th>
                  <th className="text-right p-2">kWh / day</th>
                  <th className="text-right p-2">kWh / month</th>
                </tr>
              </thead>
              <tbody>
                {data.location_energy.map((r, i) => {
                  const locKey = r.location;
                  const isLocOpen = expandedLocs.has(locKey);
                  return (
                    <FragmentAny key={i}>
                      <tr
                        className="border-t border-slate-100 cursor-pointer hover:bg-slate-50"
                        onClick={() => {
                          const next = new Set(expandedLocs);
                          if (isLocOpen) next.delete(locKey); else next.add(locKey);
                          setExpandedLocs(next);
                        }}
                        data-testid={`location-row-${i}`}
                      >
                        <td className="p-2 text-slate-400">{isLocOpen ? '▾' : '▸'}</td>
                        <td className="p-2 font-medium">{r.location}</td>
                        <td className="p-2 text-right">{r.swaps.toLocaleString()}</td>
                        <td className="p-2 text-right">{r.total_kwh.toLocaleString()} kWh</td>
                        <td className="p-2 text-right">{r.kwh_per_day.toLocaleString()} kWh</td>
                        <td className="p-2 text-right">{r.kwh_per_month.toLocaleString()} kWh</td>
                      </tr>
                      {isLocOpen && (r.by_month || []).map((m, j) => {
                        const mKey = `${locKey}|${m.month}`;
                        const isMonthOpen = expandedMonths.has(mKey);
                        return (
                          <FragmentAny key={`m-${j}`}>
                            <tr
                              className="border-t border-slate-100 bg-slate-50/60 cursor-pointer hover:bg-slate-100"
                              onClick={() => {
                                const next = new Set(expandedMonths);
                                if (isMonthOpen) next.delete(mKey); else next.add(mKey);
                                setExpandedMonths(next);
                              }}
                              data-testid={`month-row-${i}-${j}`}
                            >
                              <td className="p-2 pl-6 text-slate-400">{isMonthOpen ? '▾' : '▸'}</td>
                              <td className="p-2 pl-6 text-slate-700">{m.month}</td>
                              <td className="p-2 text-right text-slate-700">{m.swaps.toLocaleString()}</td>
                              <td className="p-2 text-right text-slate-700">{m.kwh.toLocaleString()} kWh</td>
                              <td className="p-2 text-right text-slate-500">{(m.kwh / Math.max(1, m.days)).toFixed(2)} kWh</td>
                              <td className="p-2 text-right text-slate-500">{m.days} day{m.days === 1 ? '' : 's'}</td>
                            </tr>
                            {isMonthOpen && (m.by_day || []).map((d, k) => (
                              <tr key={`d-${k}`} className="border-t border-slate-100 bg-slate-50/30 text-xs">
                                <td className="p-2"></td>
                                <td className="p-2 pl-10 text-slate-600">{d.day}</td>
                                <td className="p-2 text-right text-slate-600">{d.swaps.toLocaleString()}</td>
                                <td className="p-2 text-right text-slate-600">{d.kwh.toLocaleString()} kWh</td>
                                <td className="p-2 text-right text-slate-400">{d.kwh.toLocaleString()} kWh</td>
                                <td className="p-2"></td>
                              </tr>
                            ))}
                          </FragmentAny>
                        );
                      })}
                    </FragmentAny>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="text-base">Range per motorcycle (SoC-normalized)</CardTitle>
            <div className="flex items-center gap-2 w-full sm:w-72">
              <Input
                placeholder="Search bike no…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="bikes-search-input"
                className="h-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent style={{ height: 300 }}>
          <ResponsiveContainer>
            <BarChart data={visibleRows.slice(0, 15)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="bike" tick={{ fontSize: 11 }} interval={0} angle={-25} textAnchor="end" height={70} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="total_km" fill="#84cc16" name="Total km" />
              <Bar dataKey="avg_range_km" fill="#0ea5e9" name="Avg range @100% SoC (km)" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
      <DataTable
        testid="analysis-bikes-table"
        rows={visibleRows}
        sort={sort}
        onSortChange={handleSort}
        onRowClick={(r) => setDetailBike(r.bike)}
        columns={[
          { key: 'bike', label: 'Motorcycle', sortable: true },
          { key: 'swaps', label: 'Swaps', align: 'right', sortable: true },
          { key: 'first_odo', label: 'First odo (km)', align: 'right', sortable: true },
          { key: 'last_odo', label: 'Last odo (km)', align: 'right', sortable: true },
          { key: 'total_km', label: 'Total km', align: 'right', sortable: true },
          {
            key: 'avg_range_km',
            label: 'Avg range @100% SoC',
            align: 'right',
            sortable: true,
            render: r => r.avg_range_km == null
              ? <span className="text-slate-300">—</span>
              : `${r.avg_range_km} km`,
          },
          {
            key: 'avg_soc_drop_pct',
            label: 'Avg SoC drop',
            align: 'right',
            sortable: true,
            render: r => r.avg_soc_drop_pct == null
              ? <span className="text-slate-300">—</span>
              : `${r.avg_soc_drop_pct}%`,
          },
          {
            key: 'anomalies',
            label: 'Anomalies',
            align: 'right',
            sortable: true,
            render: r => r.anomalies > 0
              ? <Badge className="bg-rose-100 text-rose-700 border border-rose-200">{r.anomalies}</Badge>
              : <span className="text-slate-400">0</span>
          },
        ]}
      />
      <BikeDetailModal bike={detailBike} onClose={() => setDetailBike(null)} />
    </div>
  );
};

const RidersTab = ({ data }) => {
  if (!data) return <div className="p-8 text-center text-slate-400">Loading…</div>;
  const rows = data.rows || [];
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle className="text-base">Top riders by km</CardTitle></CardHeader>
        <CardContent style={{ height: 300 }}>
          <ResponsiveContainer>
            <BarChart data={rows.slice(0, 15)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis dataKey="rider" type="category" tick={{ fontSize: 11 }} width={140} />
              <Tooltip />
              <Bar dataKey="km" fill="#8b5cf6" name="km" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
      <DataTable
        testid="analysis-riders-table"
        rows={rows}
        columns={[
          { key: 'rider', label: 'Rider' },
          { key: 'swaps', label: 'Swaps', align: 'right' },
          { key: 'km', label: 'km', align: 'right' },
          { key: 'bikes', label: 'Motorcycles used', align: 'right' },
          { key: 'anomalies', label: 'Anomalies', align: 'right' },
        ]}
      />
    </div>
  );
};

const StaffTab = ({ data }) => {
  if (!data) return <div className="p-8 text-center text-slate-400">Loading…</div>;
  const rows = data.rows || [];
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle className="text-base">Swaps handled per technician</CardTitle></CardHeader>
        <CardContent style={{ height: 260 }}>
          <ResponsiveContainer>
            <BarChart data={rows}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="staff" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={70} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="swaps" fill="#f59e0b" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
      <DataTable
        testid="analysis-staff-table"
        rows={rows}
        columns={[
          { key: 'staff', label: 'Technician' },
          { key: 'swaps', label: 'Swaps', align: 'right' },
          { key: 'riders_served', label: 'Riders served', align: 'right' },
          { key: 'locations', label: 'Locations', align: 'right' },
          { key: 'anomalies', label: 'Anomalies flagged', align: 'right' },
        ]}
      />
    </div>
  );
};

const BatteriesTab = ({ data }) => {
  if (!data) return <div className="p-8 text-center text-slate-400">Loading…</div>;
  const rows = data.rows || [];
  const hist = data.soc_drop_histogram || [];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base">Battery utilisation (top 15)</CardTitle></CardHeader>
          <CardContent style={{ height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={rows.slice(0, 15)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="battery" tick={{ fontSize: 11 }} interval={0} angle={-30} textAnchor="end" height={70} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="issued_count" fill="#84cc16" name="Issued" />
                <Bar dataKey="returned_count" fill="#ef4444" name="Returned" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">SoC drop distribution</CardTitle></CardHeader>
          <CardContent style={{ height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={hist}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#0ea5e9" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
      <DataTable
        testid="analysis-batteries-table"
        rows={rows}
        columns={[
          { key: 'battery', label: 'Battery' },
          { key: 'issued_count', label: 'Issued', align: 'right' },
          { key: 'returned_count', label: 'Returned', align: 'right' },
          { key: 'total_swaps', label: 'Total swaps', align: 'right' },
          { key: 'avg_issued_soc', label: 'Avg issued SoC', align: 'right' },
          { key: 'avg_return_soc', label: 'Avg return SoC', align: 'right' },
        ]}
      />
    </div>
  );
};

const CostBreakdown = ({ k, basis, decimals = 2 }) => {
  if (!basis || basis <= 0) return null;
  const total = k.total_cost || 0;
  if (!total) return null;
  const elec = k.electricity_share || 0;
  const rent = k.rent_share || 0;
  const sal = k.staff_salary_total || 0;
  const fmt = (v) => Number((v / basis).toFixed(decimals)).toLocaleString(undefined, { maximumFractionDigits: decimals });
  const pct = (v) => total > 0 ? Math.round((v / total) * 100) : 0;
  const rows = [
    { label: 'Electricity', val: elec },
    { label: 'Rent', val: rent },
    { label: 'Salary', val: sal },
  ];
  return (
    <div className="text-[11px] opacity-80 mt-1 space-y-0.5" data-testid="cost-breakdown">
      {rows.map(r => (
        <div key={r.label} className="flex items-center justify-between gap-2 leading-tight">
          <span>{r.label}</span>
          <span className="tabular-nums">AED {fmt(r.val)} <span className="opacity-60">({pct(r.val)}%)</span></span>
        </div>
      ))}
    </div>
  );
};

const CostEnergyTab = ({ data }) => {
  if (!data) return <div className="p-8 text-center text-slate-400">Loading…</div>;
  const k = data.kpis || {};
  const elecRows = data.electricity_rows || [];
  const rentRows = data.rent_rows || [];
  const staffRows = data.staff_rows || [];
  const trend = data.cost_trend || [];
  return (
    <div className="space-y-4">
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
        <b>Per-location %</b> — each rental property carries its own <code>shared_pct_electricity</code> and <code>shared_pct_rent</code>. Edit them in <b>Accounts → Rental Expenses → Edit property</b>. Missing values default to 100 %.
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Electricity total" value={`AED ${(k.electricity_total || 0).toLocaleString()}`} sub={`Share: AED ${(k.electricity_share || 0).toLocaleString()}`} tone="amber" icon={DollarSign} />
        <StatCard label="Rent total" value={`AED ${(k.rent_total || 0).toLocaleString()}`} sub={`Share: AED ${(k.rent_share || 0).toLocaleString()}`} tone="violet" icon={DollarSign} />
        <StatCard label="Staff salary" value={`AED ${(k.staff_salary_total || 0).toLocaleString()}`} sub={`Pro-rated to ${k.window_days} days`} tone="slate" icon={Users} />
        <StatCard label="Total cost" value={`AED ${(k.total_cost || 0).toLocaleString()}`} sub={`${k.window_days} days`} tone="rose" icon={DollarSign} />
        <StatCard label="Total km" value={(k.total_km || 0).toLocaleString()} sub={`${k.total_swaps} swaps`} tone="sky" icon={Bike} />
        <StatCard
          label="Cost / swap"
          value={`AED ${k.cost_per_swap ?? 0}`}
          tone="lime"
          sub={<CostBreakdown k={k} basis={k.total_swaps} />}
        />
        <StatCard
          label="Cost / km"
          value={`AED ${k.cost_per_km ?? 0}`}
          tone="lime"
          sub={<CostBreakdown k={k} basis={k.total_km} decimals={4} />}
        />
        <StatCard
          label="Avg cost / motorcycle / month"
          value={`AED ${(k.cost_per_motorcycle_per_month ?? 0).toLocaleString()}`}
          tone="lime"
          icon={Bike}
          sub={
            k.unique_motorcycles
              ? `${k.unique_motorcycles} motorcycle${k.unique_motorcycles === 1 ? '' : 's'} · ${k.months_in_window} months`
              : '—'
          }
        />
        <StatCard
          label="Avg swaps / bike / day"
          value={(k.avg_swaps_per_bike_per_day ?? 0).toLocaleString(undefined, { maximumFractionDigits: 3 })}
          sub={`${k.unique_motorcycles || 0} motorcycles · ${k.window_days || 0} days`}
          icon={Activity} tone="sky"
        />
        <StatCard
          label="Avg swaps / bike / month"
          value={(k.avg_swaps_per_bike_per_month ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
          sub={`${k.months_in_window || 0} months in window`}
          icon={Activity} tone="violet"
        />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Cost trend</CardTitle>
            <span className="text-xs text-slate-500">Electricity + Rent + Salary + Unit economics, per month</span>
          </div>
        </CardHeader>
        <CardContent style={{ height: 300 }}>
          <ResponsiveContainer>
            <LineChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              {/* Left axis — total AED for the three cost stacks. Right
                  axis — unit economics (cost/km sits at ~AED 0.1-2 while
                  cost/swap is in the AED 10-100 range), so a shared axis
                  would flatten both to zero next to the salary line. */}
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v, name) => {
                if (name === 'Cost / km') return [`AED ${Number(v).toFixed(4)}`, name];
                if (name === 'Cost / swap') return [`AED ${Number(v).toFixed(2)}`, name];
                return [`AED ${Number(v).toLocaleString()}`, name];
              }} />
              <Legend />
              <Line yAxisId="left"  type="monotone" dataKey="electricity"   stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} name="Electricity" />
              <Line yAxisId="left"  type="monotone" dataKey="rent"          stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} name="Rent" />
              <Line yAxisId="left"  type="monotone" dataKey="salary"        stroke="#0ea5e9" strokeWidth={2} dot={{ r: 3 }} name="Salary" />
              <Line yAxisId="right" type="monotone" dataKey="cost_per_swap" stroke="#10b981" strokeWidth={2} strokeDasharray="4 3" dot={{ r: 3 }} name="Cost / swap" />
              <Line yAxisId="right" type="monotone" dataKey="cost_per_km"   stroke="#ef4444" strokeWidth={2} strokeDasharray="4 3" dot={{ r: 3 }} name="Cost / km" />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Cost breakdown by location */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Battery type utilisation</CardTitle>
            <span className="text-xs text-slate-500">Swaps per month split by battery capacity (Ah)</span>
          </div>
        </CardHeader>
        <CardContent style={{ height: 300 }}>
          <ResponsiveContainer>
            <BarChart data={data.battery_type_usage || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={v => `${Number(v).toLocaleString()} batteries`} />
              <Legend />
              {/* Stacked so month-total = total batteries dispensed */}
              <Bar dataKey="32 Ah"   stackId="ah" fill="#f59e0b" name="32 Ah" />
              <Bar dataKey="46 Ah"   stackId="ah" fill="#10b981" name="46 Ah" />
              <Bar dataKey="47.8 Ah" stackId="ah" fill="#3b82f6" name="47.8 Ah" />
              <Bar dataKey="Unknown" stackId="ah" fill="#94a3b8" name="Unknown" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Cost breakdown by location */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Cost breakdown by location</CardTitle>
            <span className="text-xs text-slate-500">Electricity + Rent + Salary share per property</span>
          </div>
        </CardHeader>
        <CardContent>
          {(data.cost_by_location || []).length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-400">
              No cost data by location yet — add rental properties or utility bills to populate.
            </div>
          ) : (
            <>
              <div style={{ height: 260 }} className="mb-3">
                <ResponsiveContainer>
                  <BarChart data={data.cost_by_location.slice(0, 15)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="location" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={70} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={v => `AED ${Number(v).toLocaleString()}`} />
                    <Legend />
                    <Bar dataKey="electricity" stackId="cost" fill="#f59e0b" name="Electricity" />
                    <Bar dataKey="rent" stackId="cost" fill="#8b5cf6" name="Rent" />
                    <Bar dataKey="staff_salary" stackId="cost" fill="#0ea5e9" name="Salary" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <DataTable
                testid="analysis-cost-by-location"
                rows={data.cost_by_location}
                columns={[
                  { key: 'location', label: 'Location / Property' },
                  { key: 'electricity', label: 'Electricity (share)', align: 'right', render: r => `AED ${(r.electricity||0).toLocaleString()}` },
                  { key: 'rent', label: 'Rent (share)', align: 'right', render: r => `AED ${(r.rent||0).toLocaleString()}` },
                  { key: 'staff_salary', label: 'Salary (share)', align: 'right', render: r => `AED ${(r.staff_salary||0).toLocaleString()}` },
                  { key: 'total', label: 'Total', align: 'right', render: r => `AED ${(r.total||0).toLocaleString()}` },
                ]}
              />
            </>
          )}
        </CardContent>
      </Card>

      <div className="space-y-3">
        <CollapsibleMonthlyTable
          title="Electricity bills in window"
          rows={elecRows}
          groupKey="month"
          columns={[
            { key: 'provider', label: 'Provider' },
            { key: 'paid_for', label: 'Paid for' },
            { key: 'amount', label: 'Amount', align: 'right', render: r => `AED ${(r.amount||0).toLocaleString()}` },
            { key: 'shared_pct', label: '%', align: 'right', render: r => `${r.shared_pct ?? '—'}%` },
            { key: 'share', label: 'Attributed', align: 'right', render: r => `AED ${(r.share||0).toLocaleString()}` },
          ]}
          amountKey="share"
          testid="analysis-elec-monthly"
        />
        <CollapsibleMonthlyTable
          title="Rental properties (pro-rata)"
          rowsByMonth={data.rent_by_month || {}}
          columns={[
            { key: 'property', label: 'Property' },
            { key: 'type', label: 'Type' },
            { key: 'contract_value', label: 'Contract', align: 'right', render: r => `AED ${(r.contract_value||0).toLocaleString()}` },
            { key: 'prorated', label: 'Pro-rata', align: 'right', render: r => `AED ${(r.prorated||0).toLocaleString()}` },
            { key: 'shared_pct', label: '%', align: 'right', render: r => `${r.shared_pct ?? '—'}%` },
            { key: 'share', label: 'Attributed', align: 'right', render: r => `AED ${(r.share||0).toLocaleString()}` },
          ]}
          amountKey="share"
          testid="analysis-rent-monthly"
        />
        <CollapsibleMonthlyTable
          title="Staff salary (prorated)"
          rowsByMonth={data.staff_by_month || {}}
          columns={[
            { key: 'staff', label: 'Staff' },
            { key: 'location', label: 'Location' },
            { key: 'monthly_salary', label: 'Monthly', align: 'right', render: r => `AED ${(r.monthly_salary||0).toLocaleString()}` },
            { key: 'prorated', label: 'Prorated', align: 'right', render: r => `AED ${(r.prorated||0).toLocaleString()}` },
          ]}
          amountKey="prorated"
          testid="analysis-staff-monthly"
        />
      </div>
    </div>
  );
};

/**
 * Collapsible monthly summary table — mirrors the drill-down UX used in
 * the "kWh dispensed by swap location" table. Renders one accordion row
 * per YYYY-MM, showing the sum of `amountKey` for that month in the
 * header and expanding into a flat detail table on click. Consumers can
 * either pass a flat `rows` array + `groupKey='month'` OR a pre-bucketed
 * `rowsByMonth` map — whichever is more convenient for the endpoint.
 */
const CollapsibleMonthlyTable = ({ title, rows, rowsByMonth, groupKey, columns, amountKey, testid }) => {
  const byMonth = useMemo(() => {
    if (rowsByMonth) return rowsByMonth;
    const map = {};
    (rows || []).forEach(r => {
      const mm = r[groupKey || 'month'] || 'Unspecified';
      if (!map[mm]) map[mm] = [];
      map[mm].push(r);
    });
    return map;
  }, [rows, rowsByMonth, groupKey]);
  const months = Object.keys(byMonth).sort();
  const [openMonths, setOpenMonths] = useState(() => new Set());
  const toggle = (mm) => {
    const next = new Set(openMonths);
    if (next.has(mm)) next.delete(mm); else next.add(mm);
    setOpenMonths(next);
  };
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent className="p-0">
        {months.length === 0 ? (
          <div className="p-4 text-sm text-slate-400">No data in window</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {months.map(mm => {
              const monthRows = byMonth[mm] || [];
              const monthTotal = monthRows.reduce((s, r) => s + (Number(r[amountKey]) || 0), 0);
              const isOpen = openMonths.has(mm);
              return (
                <div key={mm} data-testid={`${testid}-${mm}`}>
                  <button
                    className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-slate-50"
                    onClick={() => toggle(mm)}
                    data-testid={`${testid}-toggle-${mm}`}
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-slate-400 w-3 inline-block">{isOpen ? '▾' : '▸'}</span>
                      <span className="font-medium text-slate-700">{mm}</span>
                      <span className="text-xs text-slate-400">{monthRows.length} row{monthRows.length === 1 ? '' : 's'}</span>
                    </span>
                    <span className="tabular-nums text-slate-600">AED {Number(monthTotal).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                  </button>
                  {isOpen && (
                    <div className="bg-slate-50/50">
                      <DataTable
                        testid={`${testid}-detail-${mm}`}
                        rows={monthRows}
                        columns={columns}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const DataOutliersTab = ({ data }) => {
  if (!data) return <div className="p-8 text-center text-slate-400">Loading…</div>;
  const t = data.totals || {};
  const byKind = data.by_kind || [];
  const bySev = data.by_severity || [];
  const rows = data.anomalies || [];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total swaps" value={t.swaps ?? 0} icon={Activity} tone="sky" />
        <StatCard label="Anomalies" value={t.anomalies ?? 0} icon={AlertTriangle} tone="rose" />
        <StatCard label="Completeness" value={`${t.completeness_pct ?? 0}%`} sub="Clean records" tone="lime" />
        <StatCard label="High severity" value={bySev.find(s => s.severity === 'high')?.count || 0} tone="rose" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base">By anomaly kind</CardTitle></CardHeader>
          <CardContent style={{ height: 260 }}>
            {byKind.length === 0 ? <div className="h-full flex items-center justify-center text-sm text-slate-400">Clean — no anomalies</div> :
            <ResponsiveContainer>
              <BarChart data={byKind}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="kind" tick={{ fontSize: 11 }} tickFormatter={(k) => KIND_LABELS[k] || k} interval={0} angle={-15} textAnchor="end" height={70} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip formatter={(v, _n, p) => [v, KIND_LABELS[p.payload.kind] || p.payload.kind]} />
                <Bar dataKey="count" fill="#ef4444" />
              </BarChart>
            </ResponsiveContainer>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">By severity</CardTitle></CardHeader>
          <CardContent style={{ height: 260 }}>
            {bySev.length === 0 ? <div className="h-full flex items-center justify-center text-sm text-slate-400">Clean — no anomalies</div> :
            <ResponsiveContainer>
              <PieChart>
                <Pie data={bySev} dataKey="count" nameKey="severity" cx="50%" cy="50%" outerRadius={90} label={({severity,count}) => `${severity}: ${count}`}>
                  {bySev.map((s, i) => <Cell key={i} fill={SEVERITY_COLORS[s.severity] || '#94a3b8'} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>}
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base">Anomaly log</CardTitle></CardHeader>
        <CardContent>
          <DataTable
            testid="analysis-anomalies-log"
            rows={rows}
            columns={[
              { key: 'swap_date', label: 'Date' },
              { key: 'bike', label: 'Motorcycle' },
              { key: 'rider', label: 'Rider' },
              { key: 'kind', label: 'Type',
                render: r => <Badge style={{ backgroundColor: SEVERITY_COLORS[r.severity] || '#94a3b8', color: 'white' }} className="text-[10px]">
                  {KIND_LABELS[r.kind] || r.kind}
                </Badge>
              },
              { key: 'severity', label: 'Severity' },
              { key: 'message', label: 'Message' },
              { key: 'value', label: 'Value', align: 'right' },
            ]}
          />
        </CardContent>
      </Card>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Maintenance Tab — analysis of maintenance tickets.
//
// Uses its OWN date-range selector (1D / 1W / 1M / 1Y / All) because the
// business questions (repair frequency, tyre life etc.) are naturally
// time-bucketed and independent of the top swap-date filter bar.
// ---------------------------------------------------------------------------
const MaintenanceTab = () => {
  const [range, setRange] = useState('1m');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/analysis/maintenance?range=${range}`, { headers: getAuthHeader() });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error('Failed to load maintenance analysis');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [range]);

  const RANGES = [
    { key: '1d', label: '1 Day' },
    { key: '1w', label: '1 Week' },
    { key: '1m', label: '1 Month' },
    { key: '1y', label: '1 Year' },
    { key: 'all', label: 'Till date' },
  ];

  const k = data?.kpis || {};
  const tyre = data?.tyre_km?.either || {};
  const tyreFront = data?.tyre_km?.front || {};
  const tyreRear = data?.tyre_km?.rear || {};
  const brake = data?.brake_km?.either || {};
  const brakeFront = data?.brake_km?.front || {};
  const brakeRear = data?.brake_km?.rear || {};

  return (
    <div className="space-y-4" data-testid="analysis-maintenance-tab">
      {/* Range selector */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1 bg-white rounded-lg border border-slate-200 p-1">
          {RANGES.map(r => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                range === r.key ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
              }`}
              data-testid={`maintenance-range-${r.key}`}
            >
              {r.label}
            </button>
          ))}
        </div>
        {loading && <Badge className="bg-sky-100 text-sky-700 border border-sky-200"><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Loading…</Badge>}
      </div>

      {/* Headline KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total maintenance cost" value={`AED ${(k.total_cost || 0).toLocaleString()}`} sub={`${k.ticket_count || 0} tickets`} tone="lime" icon={Wrench} />
        <StatCard label="AMC / free service" value={k.amc_count || 0} sub={`~AED ${(k.amc_savings_estimate || 0).toLocaleString()} covered`} tone="sky" icon={CheckCircle2} />
        <StatCard label="Motorcycles serviced" value={k.unique_bikes || 0} sub={`${k.unique_locations || 0} locations`} tone="slate" icon={Bike} />
        <StatCard label="Avg cost / ticket" value={`AED ${(k.avg_cost_per_ticket || 0).toLocaleString()}`} tone="lime" />
      </div>

      {/* Tyre + Brake average life */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card>
          <CardHeader><CardTitle className="text-base">Tyre replacement — average KM per motorcycle</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-md bg-slate-50 p-3">
                <div className="text-[11px] uppercase text-slate-500">Front</div>
                <div className="text-xl font-bold tabular-nums">{(tyreFront.average_km || 0).toLocaleString()} km</div>
                <div className="text-[10px] text-slate-500">{tyreFront.sample_size_bikes || 0} bikes</div>
              </div>
              <div className="rounded-md bg-slate-50 p-3">
                <div className="text-[11px] uppercase text-slate-500">Rear</div>
                <div className="text-xl font-bold tabular-nums">{(tyreRear.average_km || 0).toLocaleString()} km</div>
                <div className="text-[10px] text-slate-500">{tyreRear.sample_size_bikes || 0} bikes</div>
              </div>
              <div className="rounded-md bg-lime-50 p-3">
                <div className="text-[11px] uppercase text-lime-700">Either</div>
                <div className="text-xl font-bold tabular-nums text-lime-700">{(tyre.average_km || 0).toLocaleString()} km</div>
                <div className="text-[10px] text-lime-700">{tyre.sample_size_bikes || 0} bikes</div>
              </div>
            </div>
            <div className="text-[11px] text-slate-500 italic">
              Uses full history — km measured between successive tyre-replacement tickets using cumulative odometer readings (odo-reset safe).
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Brake pad replacement — average KM per motorcycle</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-md bg-slate-50 p-3">
                <div className="text-[11px] uppercase text-slate-500">Front</div>
                <div className="text-xl font-bold tabular-nums">{(brakeFront.average_km || 0).toLocaleString()} km</div>
                <div className="text-[10px] text-slate-500">{brakeFront.sample_size_bikes || 0} bikes</div>
              </div>
              <div className="rounded-md bg-slate-50 p-3">
                <div className="text-[11px] uppercase text-slate-500">Rear</div>
                <div className="text-xl font-bold tabular-nums">{(brakeRear.average_km || 0).toLocaleString()} km</div>
                <div className="text-[10px] text-slate-500">{brakeRear.sample_size_bikes || 0} bikes</div>
              </div>
              <div className="rounded-md bg-lime-50 p-3">
                <div className="text-[11px] uppercase text-lime-700">Either</div>
                <div className="text-xl font-bold tabular-nums text-lime-700">{(brake.average_km || 0).toLocaleString()} km</div>
                <div className="text-[10px] text-lime-700">{brake.sample_size_bikes || 0} bikes</div>
              </div>
            </div>
            <div className="text-[11px] text-slate-500 italic">
              Uses full history — km measured between successive brake-pad tickets using cumulative odometer readings.
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Location-wise + Bike-wise cost */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card>
          <CardHeader><CardTitle className="text-base">Location-wise repairs</CardTitle></CardHeader>
          <CardContent style={{ height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={(data?.by_location || []).slice(0, 12)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="location" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={70} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={v => typeof v === 'number' ? `AED ${v.toLocaleString()}` : v} />
                <Legend />
                <Bar dataKey="cost" fill="#84cc16" name="Cost (AED)" />
                <Bar dataKey="count" fill="#0ea5e9" name="Tickets" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Top 10 motorcycles by repair cost</CardTitle></CardHeader>
          <CardContent>
            <DataTable
              testid="maintenance-by-bike"
              rows={(data?.by_bike || []).slice(0, 10)}
              columns={[
                { key: 'bike', label: 'Plate' },
                { key: 'location', label: 'Location' },
                { key: 'count', label: 'Tickets', align: 'right' },
                { key: 'cost', label: 'Cost', align: 'right', render: r => `AED ${(r.cost || 0).toLocaleString()}` },
              ]}
            />
          </CardContent>
        </Card>
      </div>

      {/* Repair-item cost + Most repaired items */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card>
          <CardHeader><CardTitle className="text-base">Repair item — cost breakdown</CardTitle></CardHeader>
          <CardContent>
            <DataTable
              testid="maintenance-by-item"
              rows={(data?.by_item || []).slice(0, 15)}
              columns={[
                { key: 'item', label: 'Repair item' },
                { key: 'count', label: 'Qty', align: 'right' },
                { key: 'cost', label: 'Cost', align: 'right', render: r => `AED ${(r.cost || 0).toLocaleString()}` },
              ]}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Most repaired items</CardTitle></CardHeader>
          <CardContent style={{ height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={(data?.most_repaired || []).slice(0, 10)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="item" tick={{ fontSize: 10 }} width={130} />
                <Tooltip />
                <Bar dataKey="count" fill="#8b5cf6" name="Times replaced" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Technician-wise */}
      <Card>
        <CardHeader><CardTitle className="text-base">Technician-wise repairs</CardTitle></CardHeader>
        <CardContent>
          <DataTable
            testid="maintenance-by-technician"
            rows={data?.by_technician || []}
            columns={[
              { key: 'technician', label: 'Technician' },
              { key: 'count', label: 'Tickets', align: 'right' },
              { key: 'cost', label: 'Total cost', align: 'right', render: r => `AED ${(r.cost || 0).toLocaleString()}` },
            ]}
          />
        </CardContent>
      </Card>
    </div>
  );
};



// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Analysis({ user }) {
  const [tab, setTab] = useState('overview');
  const [topTab, setTopTab] = useState('battery-swap');
  const [filters, setFilters] = useState({});
  const [options, setOptions] = useState({});
  const [tabData, setTabData] = useState({});
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({ rent_shared_pct: 100, electricity_shared_pct: 100 });

  // Drill-down modal state (populated when a KPI card is clicked).
  const [drillKind, setDrillKind] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const openDrillDown = async (kind) => {
    setDrillKind(kind);
    setDrillData(null);
    setDrillLoading(true);
    try {
      const p = new URLSearchParams();
      p.append('kind', kind);
      Object.entries(filters).forEach(([k, v]) => { if (v) p.append(k, v); });
      const res = await axios.get(`${API}/analysis/drill-down?${p.toString()}`, { headers: getAuthHeader() });
      setDrillData(res.data);
    } catch (e) {
      toast.error(`Failed to load ${kind} drill-down`);
    } finally {
      setDrillLoading(false);
    }
  };

  // Load filter options + settings once
  useEffect(() => {
    (async () => {
      try {
        const [opts, s] = await Promise.all([
          axios.get(`${API}/analysis/filter-options`, { headers: getAuthHeader() }),
          axios.get(`${API}/analysis/settings`, { headers: getAuthHeader() }),
        ]);
        setOptions(opts.data || {});
        setSettings(s.data || settings);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const queryParams = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.append(k, v); });
    return p;
  }, [filters]);

  const fetchTab = async (which) => {
    setLoading(true);
    try {
      const url = `${API}/analysis/${which}?${queryParams.toString()}`;
      const finalUrl = which === 'cost-energy'
        ? `${url}${queryParams.toString() ? '&' : ''}rent_shared_pct=${settings.rent_shared_pct}&electricity_shared_pct=${settings.electricity_shared_pct}`
        : url;
      const res = await axios.get(finalUrl, { headers: getAuthHeader() });
      setTabData(prev => ({ ...prev, [which]: res.data }));
    } catch (e) {
      console.error(e);
      toast.error(`Failed to load ${which}`);
    } finally {
      setLoading(false);
    }
  };

  // Refetch current tab whenever filters or tab change. Skip while the
  // top-level tab is on Rider Analysis (its own component fetches).
  useEffect(() => {
    if (topTab === 'battery-swap') fetchTab(tab);
  }, [tab, queryParams.toString(), topTab]);

  // For cost-energy, refetch on slider change too (debounced by React state)
  useEffect(() => {
    if (tab === 'cost-energy') fetchTab('cost-energy');
  }, [settings.rent_shared_pct, settings.electricity_shared_pct]);

  const saveSettings = async () => {
    try {
      await axios.put(`${API}/analysis/settings`, settings, { headers: getAuthHeader() });
      toast.success('Default allocation saved');
    } catch (e) {
      toast.error('Failed to save settings');
    }
  };

  const refresh = () => fetchTab(tab);

  return (
    <div className="space-y-4 p-4" data-testid="analysis-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Analysis</h1>
          <p className="text-sm text-slate-500">Battery swap insights and rider / platform performance.</p>
        </div>
        {loading && <Badge className="bg-sky-100 text-sky-700 border border-sky-200"><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Loading…</Badge>}
      </div>

      {/* Top-level tabs: Battery Swap Analysis (existing) vs Rider Analysis (new) */}
      <Tabs value={topTab} onValueChange={setTopTab}>
        <TabsList className="flex w-full sm:w-auto h-auto gap-1 p-1 bg-slate-100">
          <TabsTrigger
            value="battery-swap"
            data-testid="top-tab-battery-swap"
            className="px-4 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            Battery Swap Analysis
          </TabsTrigger>
          <TabsTrigger
            value="rider"
            data-testid="top-tab-rider-analysis"
            className="px-4 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            Rider Analysis
          </TabsTrigger>
        </TabsList>

        <TabsContent value="rider" className="mt-4">
          <RiderAnalysis />
        </TabsContent>

        {/* Battery Swap Analysis — the entire pre-existing 8-tab dashboard */}
        <TabsContent value="battery-swap" className="mt-4 space-y-4">
          <FilterBar filters={filters} setFilters={setFilters} options={options} onRefresh={refresh} />

          <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap w-full h-auto justify-start gap-1 p-1">
          <TabsTrigger value="overview" data-testid="analysis-tab-overview" className="flex-1 min-w-[110px]">Overview</TabsTrigger>
          <TabsTrigger value="bikes-range" data-testid="analysis-tab-bikes-range" className="flex-1 min-w-[110px]">Motorcycles & Range</TabsTrigger>
          <TabsTrigger value="riders" data-testid="analysis-tab-riders" className="flex-1 min-w-[110px]">Riders</TabsTrigger>
          <TabsTrigger value="staff" data-testid="analysis-tab-staff" className="flex-1 min-w-[110px]">Staff</TabsTrigger>
          <TabsTrigger value="batteries" data-testid="analysis-tab-batteries" className="flex-1 min-w-[110px]">Batteries</TabsTrigger>
          <TabsTrigger value="cost-energy" data-testid="analysis-tab-cost-energy" className="flex-1 min-w-[130px]">Cost & Energy</TabsTrigger>
          <TabsTrigger value="data-outliers" data-testid="analysis-tab-data-outliers" className="flex-1 min-w-[130px]">Data & Outliers</TabsTrigger>
          <TabsTrigger value="maintenance" data-testid="analysis-tab-maintenance" className="flex-1 min-w-[110px]">Maintenance</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-4"><ExportBar data={tabData.overview} tabKey="overview" tabLabel="Overview" /><OverviewTab data={tabData.overview} onDrillDown={openDrillDown} /></TabsContent>
        <TabsContent value="bikes-range" className="mt-4"><ExportBar data={tabData['bikes-range']} tabKey="bikes-range" tabLabel="Motorcycles & Range" /><BikesTab data={tabData['bikes-range']} filters={filters} setFilters={setFilters} onRefresh={refresh} /></TabsContent>
        <TabsContent value="riders" className="mt-4"><ExportBar data={tabData.riders} tabKey="riders" tabLabel="Riders" /><RidersTab data={tabData.riders} /></TabsContent>
        <TabsContent value="staff" className="mt-4"><ExportBar data={tabData.staff} tabKey="staff" tabLabel="Staff" /><StaffTab data={tabData.staff} /></TabsContent>
        <TabsContent value="batteries" className="mt-4"><ExportBar data={tabData.batteries} tabKey="batteries" tabLabel="Batteries" /><BatteriesTab data={tabData.batteries} /></TabsContent>
        <TabsContent value="cost-energy" className="mt-4">
          <ExportBar data={tabData['cost-energy']} tabKey="cost-energy" tabLabel="Cost & Energy" />
          <CostEnergyTab data={tabData['cost-energy']} />
        </TabsContent>
        <TabsContent value="data-outliers" className="mt-4"><ExportBar data={tabData['data-outliers']} tabKey="data-outliers" tabLabel="Data & Outliers" /><DataOutliersTab data={tabData['data-outliers']} /></TabsContent>
        <TabsContent value="maintenance" className="mt-4"><MaintenanceTab /></TabsContent>
      </Tabs>
        </TabsContent>
      </Tabs>

      {/* KPI drill-down modal */}
      <Dialog open={!!drillKind} onOpenChange={(o) => { if (!o) { setDrillKind(null); setDrillData(null); }}}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="analysis-drilldown-modal">
          <DialogHeader>
            <DialogTitle className="capitalize">{(drillKind || '').replace(/-/g, ' ')} — Drill-down</DialogTitle>
            <DialogDescription>
              {drillData ? `${drillData.rows?.length ?? 0} of ${drillData.total ?? 0} shown` : 'Loading…'}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-auto flex-1">
            {drillLoading ? (
              <div className="py-12 text-center text-sm text-slate-400">Loading…</div>
            ) : drillData?.rows?.length ? (
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    {(drillData.columns || []).map(c => (
                      <th key={c} className="px-3 py-2 text-left font-medium text-slate-600 capitalize">
                        {c.replace(/_/g, ' ')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {drillData.rows.map((r, i) => (
                    <tr key={i} className="border-t hover:bg-slate-50">
                      {(drillData.columns || []).map(c => (
                        <td key={c} className="px-3 py-2 tabular-nums">
                          {r[c] == null ? <span className="text-slate-300">—</span> : String(r[c])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="py-12 text-center text-sm text-slate-400">No records to show</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
